import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Post from '../components/Post';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './PostDetail.css';
import { apiService } from '../utils/api';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import { setPageTitle, setMetaTags, generatePostMeta } from '../utils/seoUtils';

function PostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [postData, setPostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    // 初期SEO設定
    setPageTitle('投稿を読み込み中...');
    setMetaTags({
      title: '投稿を読み込み中...',
      description: '投稿の詳細を読み込んでいます。',
      type: 'article'
    });
    
    const fetchPostDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // apiServiceの代わりに直接axiosを使用して、未認証の場合もリクエストできるようにする
        const response = await axios.get(`${config.API_BASE_URL}/api/posts/${postId}`, { headers });
        
        // 投稿が削除済みかどうかを確認
        const post = response.data;
        const isDeleted = post.deleted_at || post.is_deleted || post.status === 'deleted' || post.status === 'DELETED';
        
        if (isDeleted) {
          // 削除済み投稿としてマークし、データも保持
          setPostData({...post, isDeleted: true});
          
          // 削除済み投稿のページタイトル設定
          setPageTitle('削除済みの投稿');
          setMetaTags({
            title: '削除済みの投稿',
            description: 'この投稿は削除されました。',
            type: 'article'
          });
        } else {
          setPostData(post);
          
          // SEO設定：投稿詳細のページタイトルとメタタグを設定
          const meta = generatePostMeta(post);
          setPageTitle(meta.title, false); // falseでベースタイトルを含めない（既に生成されているため）
          setMetaTags(meta);
        }
      } catch (err) {
        
        if (err.response && err.response.status === 404) {
          setNotFound(true);
          
          // 404エラーのSEO設定
          setPageTitle('投稿が見つかりません');
          setMetaTags({
            title: '投稿が見つかりません',
            description: 'お探しの投稿は削除されたか、存在しません。',
            type: 'article'
          });
        } else {
          setError('投稿の取得中にエラーが発生しました');
          
          // エラー時のSEO設定
          setPageTitle('エラーが発生しました');
          setMetaTags({
            title: 'エラーが発生しました',
            description: '投稿の読み込み中にエラーが発生しました。',
            type: 'article'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPostDetails();
  }, [postId]);

  const handleBack = () => {
    navigate(-1);
  };

  // 投稿へのコメント処理
  const handleCommentPost = (postId) => {
    if (postData) {
      // コメント数を更新
      setPostData({
        ...postData,
        comments_count: (postData.comments_count || 0) + 1
      });
    }
    // 既に詳細ページにいるので遷移は不要
  };

  return (
    <div className="postDetail">
      <div className="postDetail__header">
        <div className="postDetail__backButton" onClick={handleBack}>
          <ArrowBackIcon />
        </div>
        <h2>投稿</h2>
      </div>

      <div className="postDetail__content">
        {loading ? (
          <div className="postDetail__loading">
            <p>読み込み中...</p>
          </div>
        ) : notFound ? (
          <div className="postDetail__notFound">
            <h3>この投稿は削除されたか、存在しません</h3>
            <p>投稿が削除されたか、URLが間違っている可能性があります。</p>
            <p>通知やブックマークから辿ってきた場合は、既に投稿者によって削除された可能性があります。</p>
            <button onClick={handleBack} className="postDetail__backToHome">
              前のページに戻る
            </button>
          </div>
        ) : error ? (
          <div className="postDetail__error">
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="postDetail__retry">
              再読み込み
            </button>
            <button onClick={handleBack} className="postDetail__backToHome" style={{ marginTop: '10px' }}>
              前のページに戻る
            </button>
          </div>
        ) : postData && postData.isDeleted ? (
          <div className="postDetail__deleted">
            <div className="postDetail__deletedContent">
              <h3>この投稿は削除されました</h3>
              <p>投稿者によって削除されたか、管理者によって削除された可能性があります。</p>
              <button onClick={handleBack} className="postDetail__backToHome">
                前のページに戻る
              </button>
            </div>
          </div>
        ) : postData ? (
          <Post
            id={postData.original_post_id || postData.id}
            displayName={postData.author?.display_name || postData.display_name || ''}
            username={postData.author?.username || postData.username || ''}
            verified={postData.author?.verified || false}
            text={postData.text}
            avatar={postData.author?.avatar || postData.avatar || ''}
            image={postData.image || postData.image_url}
            video={postData.video || postData.video_url}
            createdAt={postData.created_at}
            likes={postData.likes_count}
            comments={postData.comments_count}
            retweets={postData.reposts_count || 0}
            isRetweet={postData.is_retweet || false}
            retweeter={postData.retweeter || null}
            authorId={postData.author?.id || postData.user_id}
            poll={postData.poll}
            has_poll={postData.has_poll || false}
            shifts={postData.shifts}
            isDetailPage={true}
            viewOnly={!isLoggedIn}
            onComment={handleCommentPost}
          />
        ) : null}
      </div>
    </div>
  );
}

export default PostDetail; 